import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["LOAD BOOKED", "DISPATCHED", "DRIVER ASSIGNED", "AT PICKUP", "LOADED", "IN TRANSIT", "AT DELIVERY", "DELIVERED"];
const EMPTY = {
  load_number: "", customer_name: "", customer_email: "", driver_name: "", truck_number: "", trailer_number: "",
  pickup_location: "", delivery_location: "", pickup_date: "", delivery_date: "", freight_type: "",
  weight_lbs: 0, status: "LOAD BOOKED", current_location: "", notes: "",
};

export default function Shipments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    api.get("/admin/shipments").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, weight_lbs: Number(form.weight_lbs) || 0 };
      if (editing) await api.put(`/admin/shipments/${editing.id}`, payload);
      else await api.post("/admin/shipments", payload);
      toast.success(editing ? "Shipment updated" : "Shipment created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this shipment?")) return;
    await api.delete(`/admin/shipments/${id}`); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="font-mono-lt text-xs uppercase tracking-[0.3em] text-[var(--lt-accent-bright)] mb-2">// Shipments</div>
          <h1 className="font-display font-black text-4xl text-white uppercase">Load Manager</h1>
        </div>
        <Button onClick={openNew} data-testid="new-shipment-btn" className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-xl h-11 font-head uppercase tracking-widest text-xs"><Plus className="h-4 w-4 mr-2" /> New Load</Button>
      </div>

      <div className="border border-[var(--lt-border)] bg-[var(--lt-panel)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--lt-bg)] text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-500">
            <tr>{["Load #", "Route", "Customer", "Driver", "Status", ""].map((h) => <th key={h} className="text-left p-4">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[var(--lt-border)]">
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-slate-500 text-center">No shipments yet.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} className="hover:bg-[var(--lt-panel-hover)]" data-testid={`ship-row-${it.id}`}>
                <td className="p-4 font-mono-lt text-white">{it.load_number}</td>
                <td className="p-4 text-slate-300">{it.pickup_location} → {it.delivery_location}</td>
                <td className="p-4 text-slate-300">{it.customer_name || "—"}</td>
                <td className="p-4 text-slate-300">{it.driver_name || "—"}</td>
                <td className="p-4"><span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[var(--lt-accent-bright)]" /><span className="font-head uppercase tracking-widest text-xs text-white">{it.status}</span></span></td>
                <td className="p-4 text-right"><Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-slate-400 hover:text-white"><Pencil className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => remove(it.id)} data-testid={`del-${it.id}`} className="text-slate-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--lt-panel)] border-[var(--lt-border)] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display font-black text-2xl uppercase">{editing ? "Edit Load" : "New Load"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Load Number" v={form.load_number} onC={(v) => setForm({ ...form, load_number: v })} testid="f-load" />
            <F label="Customer Name" v={form.customer_name} onC={(v) => setForm({ ...form, customer_name: v })} testid="f-customer" />
            <F label="Driver Name" v={form.driver_name} onC={(v) => setForm({ ...form, driver_name: v })} testid="f-driver" />
            <F label="Truck #" v={form.truck_number} onC={(v) => setForm({ ...form, truck_number: v })} testid="f-truck" />
            <F label="Trailer #" v={form.trailer_number} onC={(v) => setForm({ ...form, trailer_number: v })} testid="f-trailer" />
            <F label="Freight Type" v={form.freight_type} onC={(v) => setForm({ ...form, freight_type: v })} testid="f-freight" />
            <F label="Pickup Location" v={form.pickup_location} onC={(v) => setForm({ ...form, pickup_location: v })} testid="f-pickup" />
            <F label="Delivery Location" v={form.delivery_location} onC={(v) => setForm({ ...form, delivery_location: v })} testid="f-delivery" />
            <F label="Pickup Date" type="date" v={form.pickup_date} onC={(v) => setForm({ ...form, pickup_date: v })} testid="f-pdate" />
            <F label="Delivery Date" type="date" v={form.delivery_date} onC={(v) => setForm({ ...form, delivery_date: v })} testid="f-ddate" />
            <F label="Weight (lbs)" type="number" v={form.weight_lbs} onC={(v) => setForm({ ...form, weight_lbs: v })} testid="f-weight" />
            <div>
              <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-2 block">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="f-status" className="bg-[var(--lt-bg)] border-[var(--lt-border)] text-white h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <F label="Current Location" v={form.current_location} onC={(v) => setForm({ ...form, current_location: v })} testid="f-current" />
            <div className="md:col-span-2">
              <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-2 block">Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="f-notes" className="bg-[var(--lt-bg)] border-[var(--lt-border)] text-white rounded-xl" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl border-[var(--lt-border)] bg-transparent text-slate-300">Cancel</Button>
            <Button onClick={save} data-testid="save-shipment" className="rounded-xl bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white font-head uppercase tracking-widest">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, v, onC, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-2 block">{label}</Label>
      <Input type={type} value={v ?? ""} onChange={(e) => onC(e.target.value)} data-testid={testid} className="bg-[var(--lt-bg)] border-[var(--lt-border)] text-white h-11 rounded-xl" />
    </div>
  );
}
